import { Injectable } from '@angular/core';

import { Entity, Predicate, EntityQuery, FilterQueryOp } from 'breeze-client';
import { EntityManagerProvider } from './entity-manager-provider';
import { UnitOfWork } from './unit-of-work';
import { Repository } from './repository';
import { BreezeDxStoreService } from '../breeze-dx-store.service';
import { InterestRepository } from './interests.repository';
import moment from 'moment';
import { Agenda, BackgroundCheck, Campaign, CampaignAction, CampaignOptOut, CommitteeAttachment, CommitteeAttachmentEmail, CommitteeAttachmentEmailLink, CommitteeAttachmentReview, CommitteeMember, ContributionBatch, Family, Interest, InterestCategory, Involvement, InvolvementSkill, Log, Message, Nomination, NominationAssignment, OfferingSchedule, Person, Pledge, PledgeLine, TextGroupLink, TextMessage, Visit } from '../model/entity-model';
import { Entities } from './entities';

@Injectable()
export class ArkUnitOfWork extends UnitOfWork {
    lookups: any;

    agendas: Repository<Agenda>;
    calendarEvents: Repository<Entity>;
    cloverAccounts: Repository<Entity>;
    contributionAccounts: Repository<Entity>;
    contributionAccountAttachments: Repository<Entity>;
    contributionBatchNotes: Repository<Entity>;
    families: Repository<Family>;
    people: Repository<Person>;
    personInterests: Repository<Entity>;
    groups: Repository<Entity>;
    groupPersons: Repository<Entity>;
    households: Repository<Entity>;
    photos: Repository<Entity>;
    messages: Repository<Message>;
    regionSections: Repository<Entity>;
    contributionBatches: Repository<ContributionBatch>;
    contributionBatchStatuses: Repository<Entity>;
    contributionCashes: Repository<Entity>;
    contributions: Repository<Entity>;
    contributionLines: Repository<Entity>;
    pledges: Repository<Pledge>;
    pledgeLines: Repository<PledgeLine>;
    visits: Repository<Visit>;
    relationships: Repository<Entity>;
    lookupValues: Repository<Entity>;
    users: Repository<Entity>;
    interests: InterestRepository;
    interestCategories: Repository<InterestCategory>;
    textMessages: Repository<TextMessage>;
    textGroupLinks: Repository<TextGroupLink>;
    textMessageSents: Repository<Entity>;
    involvements: Repository<Involvement>;
    involvementSkills: Repository<InvolvementSkill>;
    backgroundChecks: Repository<BackgroundCheck>;
    offeringSchedules: Repository<OfferingSchedule>;
    committeeAttachments: Repository<CommitteeAttachment>;
    committeeAttachmentEmails: Repository<CommitteeAttachmentEmail>;
    committeeAttachmentEmailLinks: Repository<CommitteeAttachmentEmailLink>;
    committeeAttachmentReviews: Repository<CommitteeAttachmentReview>;
    committeeMembers: Repository<CommitteeMember>;
    nominations: Repository<Nomination>;
    nominationTallies: Repository<Entity>;
    nominationAssignments: Repository<NominationAssignment>;
    proposals: Repository<Entity>;
    proposalVotes: Repository<Entity>;
    proposalDiscussions: Repository<Entity>;
    bankTransactions: Repository<Entity>;
    bankTransactionTypes: Repository<Entity>;
    onlineGivings: Repository<Entity>;
    logs: Repository<Entity>;
    settings: Repository<Entity>;

    auctionItems: Repository<Entity>;
    auctionPaddles: Repository<Entity>;
    auctionPaddleUsers: Repository<Entity>;
    auctionPurchases: Repository<Entity>;

    invoices: Repository<Entity>;
    invoiceLines: Repository<Entity>;

    campaigns: Repository<Campaign>;
    campaignActions: Repository<CampaignAction>;
    campaignOptOuts: Repository<CampaignOptOut>;


    constructor(emProvider: EntityManagerProvider, private storeService: BreezeDxStoreService) {
        super(emProvider);

        this.lookups = Entities.lookups;
        emProvider.prepare().then((lookups) => {
            this.lookups = lookups;
        });

        this.agendas = this.createRepository<Agenda>('Agenda', 'agendas');
        this.backgroundChecks = this.createRepository<BackgroundCheck>('BackgroundCheck', 'backgroundChecks');
        this.calendarEvents = this.createRepository<Entity>('CalendarEvent', 'calendarEvents');
        this.cloverAccounts = this.createRepository<Entity>('CloverAccount', 'cloverAccounts');
        this.contributionAccounts = this.createRepository<Entity>('ContributionAccount', 'contributionAccounts');
        this.contributionAccountAttachments = this.createRepository<Entity>('ContributionAccountAttachment', 'contributionAccountAttachments');
        this.contributionBatchNotes = this.createRepository<Entity>('ContributionBatchNote', 'contributionBatchNotes');
        this.families = this.createRepository<Family>('Family', 'families');
        this.people = this.createRepository<Person>('Person', 'people');
        this.personInterests = this.createRepository<Entity>('PersonInterest', 'personInterests');
        this.interests = new InterestRepository(this.manager);
        this.interestCategories = this.createRepository<InterestCategory>('InterestCategory', 'interestCategories');
        this.involvements = this.createRepository<Involvement>('Involvement', 'involvements');
        this.involvementSkills = this.createRepository<InvolvementSkill>('InvolvementSkill', 'involvementSkills');
        this.groups = this.createRepository<Entity>('Group', 'groups');
        this.groupPersons = this.createRepository<Entity>('GroupPerson', 'groupPersons');
        this.households = this.createRepository<Entity>('Household', 'households');
        this.photos = this.createRepository<Entity>('Photo', 'photos');
        this.messages = this.createRepository<Message>('Message', 'messages');
        this.regionSections = this.createRepository<Entity>('RegionSection', 'regionSections');
        this.contributionBatches = this.createRepository<ContributionBatch>('ContributionBatch', 'contributionBatches');
        this.contributionBatchStatuses = this.createRepository<Entity>('ContributionBatchStatus', 'contributionBatchStatuses');
        this.contributionCashes = this.createRepository<Entity>('ContributionCash', 'contributionCashes');
        this.contributions = this.createRepository<Entity>('Contribution', 'contributions');
        this.contributionLines = this.createRepository<Entity>('ContributionLine', 'contributionLines');
        this.pledges = this.createRepository<Pledge>('Pledge', 'pledges');
        this.pledgeLines = this.createRepository<PledgeLine>('PledgeLine', 'pledgeLines');
        this.relationships = this.createRepository<Entity>('Relationship', 'relationships');
        this.lookupValues = this.createRepository<Entity>('LookupValue', 'lookupValues');
        this.users = this.createRepository<Entity>('User', 'users');
        this.visits = this.createRepository<Visit>('Visit', 'visits');
        this.textMessages = this.createRepository<TextMessage>('TextMessage', 'textMessages');
        this.textGroupLinks = this.createRepository<TextGroupLink>('TextGroupLink', 'textGroupLinks');
        this.textMessageSents = this.createRepository<Entity>('TextMessageSent', 'textMessageSents');
        this.offeringSchedules = this.createRepository<OfferingSchedule>('OfferingSchedule', 'offeringSchedules');
        this.committeeMembers = this.createRepository<CommitteeMember>('CommitteeMember', 'committeeMembers');
        this.committeeAttachments = this.createRepository<CommitteeAttachment>('CommitteeAttachment', 'committeeAttachments');
        this.committeeAttachmentEmails = this.createRepository<CommitteeAttachmentEmail>('CommitteeAttachmentEmail', 'committeeAttachmentEmails');
        this.committeeAttachmentEmailLinks = this.createRepository<CommitteeAttachmentEmailLink>('CommitteeAttachmentEmailLink', 'committeeAttachmentEmailLinks');
        this.committeeAttachmentReviews = this.createRepository<CommitteeAttachmentReview>('CommitteeAttachmentReview', 'committeeAttachmentReviews');
        this.nominations = this.createRepository<Nomination>('Nomination', 'nominations');
        this.nominationTallies = this.createRepository<Entity>('NominationTally', 'nominationTallies');
        this.nominationAssignments = this.createRepository<NominationAssignment>('NominationAssignment', 'nominationAssignments');
        this.proposals = this.createRepository<Entity>('Proposal', 'proposals');
        this.proposalVotes = this.createRepository<Entity>('ProposalVote', 'proposalVotes');
        this.proposalDiscussions = this.createRepository<Entity>('ProposalDiscussion', 'proposalDiscussions');
        this.bankTransactions = this.createRepository<Entity>('BankTransaction', 'bankTransactions');
        this.bankTransactionTypes = this.createRepository<Entity>('BankTransactionType', 'bankTransactionTypes');
        this.onlineGivings = this.createRepository<Entity>('OnlineContribution', 'onlineContributions');
        this.logs = this.createRepository<Log>('Log', 'log');
        this.settings = this.createRepository<Log>('Setting', 'settings');

        this.auctionItems = this.createRepository<Log>('AuctionItem', 'auctionItems');
        this.auctionPaddles = this.createRepository<Log>('AuctionPaddle', 'auctionPaddles');
        this.auctionPaddleUsers = this.createRepository<Log>('AuctionPaddleUser', 'auctionPaddleUsers');
        this.auctionPurchases = this.createRepository<Log>('AuctionPurchase', 'auctionPurchases');
        this.invoices = this.createRepository<Log>('Invoice', 'invoices');
        this.invoiceLines = this.createRepository<Log>('InvoiceLine', 'invoiceLines');

        this.campaigns = this.createRepository<Campaign>('Campaign', 'campaigns');
        this.campaignActions = this.createRepository<CampaignAction>('CampaignAction', 'campaignActions');
        this.campaignOptOuts = this.createRepository<CampaignOptOut>('CampaignOptOut', 'campaignOptOuts');
    }

    addEntity(entity) {
        this.manager.detachEntity(entity);
        this.manager.addEntity(entity);
    }

    async getUsers(options?) {
        let query = EntityQuery.from('Users');
        if (options) {
            if (options.expand) {
                query = query.expand(options.expand);
            }
            if (options.orderby) {
                query = query.orderBy(options.orderby);
            }
            if (options.notId) {
                query = query.where('id', FilterQueryOp.NotEquals, options.notId);
            }
            if (options.userName) {
                query = query.where('userName', FilterQueryOp.Equals, options.userName);
            }
            if (options.active === true) {
                query = query.where('active', FilterQueryOp.Equals, true);
            }
        }
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getLogins() {
        const query = EntityQuery.from('GetLogins').take(500);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getFamilies() {
        // let predicate = new Predicate('includeInDirectory', '==', true).and('isActive', '==', true).and('familyStatusId', '!=', 7);
        // const query = this.families.baseQuery().where(predicate);
        const query = this.families.baseQuery();
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getAllFamilyById(id: number) {
        const query = EntityQuery.from('AllFamilies').where('id', '==', id);
        const data = await this.manager.executeQuery(query);
        return data.results[0] as Family;
    }

    async getPersonById(personId: number) {
        const query = EntityQuery.from('People').where('id', '==', personId);
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getFamilyById(familyId: number) {
        const query = EntityQuery.from('Families').expand('members').where('id', '==', familyId);
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }
    async getFamiliesInfosById(expand?: any) {
        let predicate = new Predicate('includeInDirectory', '==', true).and('isActive', '==', true).and('familyStatusId', '!=', 7);
        const query = EntityQuery.from('Families').expand(expand).where(predicate).select('id, name').orderBy('name');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getFamiliesInfos(expand?: any) {
        let predicate = new Predicate('includeInDirectory', '==', true)
            .and('isActive', '==', true)
            .and('familyStatusId', '!=', Family.Statuses.Deceased)
            .and('familyStatusId', '!=', Family.Statuses.InActiveMember)
            .and('familyStatusId', '!=', Family.Statuses.Visitor)
            .and('familyStatusId', '!=', Family.Statuses.NonMember)
            .and('familyStatusId', '!=', Family.Statuses.NonFamily);
        const query = EntityQuery.from('Families').expand(expand).where(predicate).select('id, name, householdId').orderBy('name');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getFamiliesPhotos() {
        const query = EntityQuery.from('Photos').where('isDirectoryPhoto', '==', true);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getFamilyPhotos(familyId: number) {
        const query = EntityQuery.from('Photos').where('familyId', '==', familyId);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getMembersByCommitteeId(committeeId?: number, showHistory?: boolean) {
        let predicate = new Predicate('id', '!=', null);
        if (!!committeeId && committeeId !== 0) {
            predicate = predicate.and('committeeId', '==', committeeId);
        }
        if (!showHistory) predicate = predicate.and('endDate', '==', null);
        const query = EntityQuery.from('CommitteeMembers').expand('person').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getMembersByCommitteeIds(committeeIds?: number[]) {
        let predicate = new Predicate('endDate', '==', null);
        let query = EntityQuery.from('CommitteeMembers').expand('person').where(predicate);
        query = query.where(this.whereIn('committeeId', committeeIds));

        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getBatchInfo(id: number) {
        const query = EntityQuery.from('BatchInfo').withParameters({ id });
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getBatchNotes(id: number) {
        const query = EntityQuery.from('ContributionBatchNotes').where('batchId', '==', Number(id)).orderByDesc('created');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getBatchList(take?: number) {
        let query = EntityQuery.from('BatchesList').orderByDesc('id');
        if (!!take) query = query.take(take);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    getBatchListStore() {
        const query = EntityQuery.from('BatchesList').where('statusName', '!=', 'Void');
        return this.storeService.createStore(this, this.contributionBatches, query, 'id', 'date');
    }

    async getClassisList(startDate: Date, endDate: Date, statusId: number) {
        const query = EntityQuery.from('ClassisList').withParameters({ startDate: startDate, endDate: endDate, statusId: statusId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getClassisListByBatches(startDate: Date, endDate: Date, statusId: number) {
        const query = EntityQuery.from('ClassisListByBatches').withParameters({ startDate: startDate, endDate: endDate, statusId: statusId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getBatchCashByBatchId(batchId: number) {
        const query = EntityQuery.from('ContributionCash').where('batchId', '==', batchId);
        const contributionCash = await this.executeQuery(query);
        return contributionCash[0];
    }

    async getBatchCashByContributionId(contributionId: number) {
        const query = EntityQuery.from('ContributionCash').where('contributionId', '==', contributionId);
        const contributionCash = await this.executeQuery(query);
        return contributionCash[0];
    }

    async getBatchCashSummary(batchId: number) {
        const query = EntityQuery.from('GetBatchCashSummary').withParameters({batchId: batchId});
        const contributionCash = await this.executeQuery(query);
        return contributionCash[0];
    }

    async getGeneralFund() {
        const query = EntityQuery.from('GeneralFund').orderBy('year');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getGeneralFundOverview() {
        const query = EntityQuery.from('GeneralFundOverview').orderBy('year');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getGeneralFundDistinct() {
        const query = EntityQuery.from('GeneralFundDistinct');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getReconciliationData(startDate: Date, endDate: Date, canSeePledges: boolean) {
        const query = EntityQuery.from('GetReconciliationData').withParameters({ startDate, endDate, canSeePledges });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getThanksgivingHistory() {
        const query = EntityQuery.from('ThanksgivingHistory');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOfferingTotals() {
        const query = EntityQuery.from('GetOfferingTotals').orderBy('name');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOfferingHistory() {
        const query = EntityQuery.from('GetOfferingHistory').orderBy('date');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOfferingsScheduledByDate(date: Date, am: boolean, pm: boolean) {
        let predicate = new Predicate('deleted', '==', null).and('offeringDate', '==', date).and('aMOffering', '==', am).and('pMOffering', '==', pm);
        const query = EntityQuery.from('OfferingSchedules').where(predicate).expand('specialAccount').take(1);
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getAccountDetail(accountId: number) {
        const query = EntityQuery.from('GetAccountDetail').withParameters({ accountId: accountId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getRelationships() {
        const query = EntityQuery.from('Relationships');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPersonStatuses() {
        const query = EntityQuery.from('PersonStatuses');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getVisitTypes() {
        const query = EntityQuery.from('VisitTypes').orderBy('name');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getStates() {
        const query = EntityQuery.from('LookupValues').where('type', '==', 'STATE').orderBy('text');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getMaritalStatus() {
        const query = EntityQuery.from('LookupValues').where('type', '==', 'MARITAL').orderBy('text');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getJobCodes() {
        const query = EntityQuery.from('LookupValues').where('type', '==', 'OCCUPATION').orderBy('text');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getJoinCodes() {
        const query = EntityQuery.from('LookupValues').where('type', '==', 'HOWJOINT').orderBy('text');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getTitles() {
        const query = EntityQuery.from('LookupValues').where('type', '==', 'TITLE').orderBy('text');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getFamilyAddress() {
        const query = EntityQuery.from('LookupValues').where('type', '==', 'TITLE').orderBy('text');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getHouseholds() {
        const query = EntityQuery.from('Households').orderBy('number');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getHouseholdVisits(householdId: number) {
        const query = EntityQuery.from('GetHouseholdVisits').withParameters({ householdId: householdId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPreviousVisits(personId: number) {
        const query = EntityQuery.from('Visits').where('personId', '==', personId).orderByDesc('date');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getMembersByStatus(statusId: number) {
        let predicate = new Predicate('includeInDirectory', '==', true);
        if (statusId !== 1) {
            predicate = predicate.and('personStatusId', '==', statusId);
        }
        const query = EntityQuery.from('People').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getSundaySchool() {
        const query = EntityQuery.from('GetSundaySchoolList');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getDoubleCheck(batch1Id: number, batch2Id: number) {
        const query = EntityQuery.from('GetDoubleCheck').withParameters({ batch1Id: batch1Id, batch2Id: batch2Id });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getDoubleCheckForCheckNumbers(batch1Id: number, batch2Id: number) {
        const query = EntityQuery.from('GetDoubleCheckForCheckNumbers').withParameters({ batch1Id: batch1Id, batch2Id: batch2Id });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getDoubleCheckForCashBreakdown(batchId: number) {
        const query = EntityQuery.from('GetDoubleCheckForCashBreakdown').withParameters({ batchId: batchId });
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getEmailMessageSentsByMessageId(messageId: number) {
        const query = EntityQuery.from('MessageSents').expand('person').where('messageId', '==', messageId).orderBy('person.lastName, person.firstName');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getTextMessageSentsByMessageId(messageId: number) {
        const query = EntityQuery.from('TextMessageSents').expand('person').where('messageId', '==', messageId).orderBy('person.lastName, person.firstName');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getTextMessageMemberCount() {
        const predicate = new Predicate('cellPhone', '!=', null).and('includeInDirectory', '==', true).and('isCellPhoneUnlisted', '==', false);
        const query = EntityQuery.from('People').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results.length;
    }
    // tslint:disable-next-line:member-ordering
    peopleForGiving: any[];
    getPeopleForGiving() {
        if (this.peopleForGiving) return Promise.resolve(this.peopleForGiving);

        return Promise.all([this.getPeopleWithGivingRecord(), this.getCashPerson(), this.getOtherPerson()]).then((data) => {
            this.peopleForGiving = data[0].map((person: Person) => {
                return person.getPersonInfo();
            });
            this.peopleForGiving.push({ id: data[1].id, name: 'Cash Contibutions - 9999' });
            this.peopleForGiving.push({ id: data[2].id, name: 'Other Payment - 9998' });
            this.peopleForGiving.push({ id: 0, name: '' });
            return this.peopleForGiving;
        });
    }

    // tslint:disable-next-line:member-ordering
    _peopleGiving: any[];
    async getPeopleWithGivingRecord() {
        if (this._peopleGiving) return Promise.resolve(this._peopleGiving);

        const query = EntityQuery.from('People')
            .select('id, firstName, lastName, envelopeNumber, sex')
            .where('includeForGiving', 'eq', 'true')
            .toType('Person')
            .orderBy('lastName, firstName');

        this._peopleGiving = (await this.executeQuery(query)) as any[];
        return this._peopleGiving;
    }

    // tslint:disable-next-line:member-ordering
    _people: any[];
    async getPeople() {
        if (this._people) return Promise.resolve(this._people);

        const query = EntityQuery.from('People')
            .select('id, firstName, lastName, envelopeNumber, sex')
            .where('includeInDirectory', 'eq', 'true')
            .toType('Person')
            .orderBy('lastName, firstName');

        this._people = (await this.executeQuery(query)) as any[];
        return this._people;
    }

    // tslint:disable-next-line:member-ordering
    _cashPerson;
    getCashPerson() {
        if (this._cashPerson) return Promise.resolve(this._cashPerson);

        const query = EntityQuery.from('People').select('id, firstName, lastName, envelopeNumber').where('envelopeNumber', 'eq', '9999').toType('Person');

        return this.executeQuery(query).then((data) => {
            this._cashPerson = data[0];
            return this._cashPerson;
        });
    }

    // tslint:disable-next-line:member-ordering
    _otherPerson;
    getOtherPerson() {
        if (this._otherPerson) return Promise.resolve(this._otherPerson);

        const query = EntityQuery.from('People').select('id, firstName, lastName, envelopeNumber').where('envelopeNumber', 'eq', '9998').toType('Person');

        return this.executeQuery(query).then((data) => {
            this._otherPerson = data[0];
            return this._otherPerson;
        });
    }

    getContribution(id) {
        return this.getEntityById('Contributions', id, 'lines,cashCounts,person');
    }

    getContributions(batchId, count?) {
        let query = EntityQuery.from('Contributions').where('batchId', '==', Number(batchId)).expand('lines,type,person').orderBy('created desc');
        if (count) query = query.take(count);

        return this.executeQuery(query);
    }

    includeAccounts() {
        return EntityQuery.from('ContributionAccounts')
            .where('alwaysInclude', '==', true)
            .where('active', '==', true)
            .orderBy('displayOrder, name')
            .using(this.manager)
            .executeLocally();
    }

    otherAccounts() {
        return EntityQuery.from('ContributionAccounts')
            .where('alwaysInclude', '==', false)
            .where('active', '==', true)
            .orderBy('displayOrder, name')
            .using(this.manager)
            .executeLocally()
            .map((item: any) => {
                return { id: item.id, name: item.name };
            });
    }

    async getAccounts(showInactive?: boolean) {
        let query = EntityQuery.from('ContributionAccounts')
            // .where('alwaysInclude', '==', false)
            // .where('active', '==', true)
            .orderBy('displayOrder, name');
        if (!showInactive) query = query.where('active', '==', true);
        query = query.expand('attachments');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    getContributionTypes() {
        return EntityQuery.from('ContributionTypes')
            .orderBy('name')
            .using(this.manager)
            .executeLocally()
            .map((item: any) => {
                return { id: item.id, name: item.name };
            });
    }

    async getPreviousCloverCarryovers() {
        const query = EntityQuery.from('PreviousCarryoverBatches');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPeopleForNominations() {
        const ageLimit = new Date(new Date().getFullYear() - 18, 0, 1);
        let predicate = new Predicate('includeInDirectory', '==', true);
        predicate = predicate.and('birthDate', '<', ageLimit).and('sex', '==', 'M');
        const query = EntityQuery.from('People').where(predicate).orderBy('lastName, firstName');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getNominations() {
        const yearLimit = moment(new Date()).add(-1, 'year').toDate();
        let predicate = new Predicate('deleted', '==', null);
        predicate = predicate.and('created', '>', yearLimit).and('closed', '==', null);
        const query = EntityQuery.from('Nominations').where(predicate).orderBy('id');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

        async getNominationVotes() {
        let predicate = new Predicate('deleted', '==', null).and('sent', '==', null);
        const query = EntityQuery.from('NominationVotes').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }


    async getNominationGroups() {
        const query = EntityQuery.from('GetNominationGroups');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getNominationTallies() {
        let predicate = new Predicate('deleted', '==', null).and('closed', '==', null);
        const query = EntityQuery.from('NominationTallies').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getNominationTallyGroups() {
        const query = EntityQuery.from('GetNominationTallyGroups');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getNominationSettings() {
        const query = EntityQuery.from('NominationSettings');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getSettingsByName(name: string) {
        const query = EntityQuery.from('Settings').where('name', '==', name);
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getNominationConfirmed() {
        const dateLimit = moment(new Date()).startOf('year').toDate();
        let predicate = new Predicate('deleted', '==', null);
        predicate = predicate.and('confirmed', '>', dateLimit).and('closed', '==', null);
        const query = EntityQuery.from('NominationAssignments').where(predicate).orderBy('role, person.firstName, person.lastName');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async createNominationCheck(){
        const query = EntityQuery.from('CreateNominationCheck');
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getGivingFilePath(type: any) {
        const query = EntityQuery.from('GivingAttachments').where('type', '==', type).orderByDesc('created').take(1);
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    whereIn(property: string, values: any[]) {
        let predicate: Predicate;
        values.forEach((value) => {
            if (!predicate) predicate = new Predicate(property, '==', value);
            else predicate = predicate.or(property, '==', value);
        });
        return predicate;
    }

    async getProposals(startDate?: Date, expand?: any, take?: number) {
        let predicate = new Predicate('deleted', '==', null);
        if (startDate) predicate = predicate.and('created', '>', startDate);
        let query = EntityQuery.from('Proposals').where(predicate).orderByDesc('created');
        if (expand) query = query.expand(expand);
        if (take) query = query.take(take);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getCommitteeAttachmentEmails(userName: string, expand: string) {
        let predicate = new Predicate('deleted', '==', null).and('committeeId', '!=', 20).and('sent', '!=', null);
        if (userName) predicate = predicate.and('createdBy', '==', userName);
        let query = EntityQuery.from('CommitteeAttachmentEmails').where(predicate).orderByDesc('created');
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getCommitteeAttachments(committeeId: number, expand?: any, typeId?: number) {
        let predicate = new Predicate('deleted', '==', null).and('committeeId', '==', committeeId);
        if (typeId) predicate = predicate.and('typeId', '==', typeId);
        let query = EntityQuery.from('CommitteeAttachments').where(predicate).orderByDesc('created');
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getCommitteeAttachmentReviews(personId: number, showDismissed?: boolean, expand?: any) {
        let predicate = new Predicate('deleted', '==', null).and('personId', '==', personId).and('committeeAttachment.typeId', '==', 2);
        if (!showDismissed) predicate = predicate.and('dismissed', '==', null);
        let query = EntityQuery.from('CommitteeAttachmentReviews').where(predicate).orderByDesc('sent');
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getMinutesReview() {
        let query = EntityQuery.from('GetMinutesReview');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getVoteByUserId(userId: number, proposalId: number) {
        let predicate = new Predicate('userId', '==', userId).and('proposalId', '==', proposalId);
        const query = EntityQuery.from('ProposalVotes').where(predicate).take(1);
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }
    async getVotesByProposalId(proposalId: number, expand?: any) {
        let predicate = new Predicate('deleted', '==', null);
        predicate = predicate.and('proposalId', '==', proposalId);
        let query = EntityQuery.from('ProposalVotes').where(predicate).orderByDesc('created');
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getDiscussionByProposalId(proposalId: number, expand?: any) {
        let predicate = new Predicate('deleted', '==', null);
        predicate = predicate.and('proposalId', '==', proposalId);
        let query = EntityQuery.from('ProposalDiscussions').where(predicate).orderByDesc('created');
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getBankTransactions(startDate) {
        let predicate = new Predicate('deleted', '==', null).and('bankTransactionTypeId', '==', 1).and('processed', '==', null);
        const query = EntityQuery.from('BankTransactions').expand('onlineContributions').where(predicate).orderBy('postDate');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getOnlineGivingAmounts() {
        let orPredicate = new Predicate('bankTransaction.processed', '==', null).or('bankTransactionId', '==', null);
        let predicate = new Predicate('deleted', '==', null).and(orPredicate);
        const query = EntityQuery.from('OnlineContributions').where(predicate).orderBy('batchId, donationDate');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getAnnouncements() {
        const query = EntityQuery.from('GetAnnouncements');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getBirthdays() {
        const query = EntityQuery.from('GetBirthdays');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getAnniversaries() {
        const query = EntityQuery.from('GetAnniversaries');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getCalendarEvents() {
        const query = EntityQuery.from('CalendarEvents').where('deleted', '==', null);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPledges(options?: any) {
        const predicate = new Predicate('deleted', '==', null);
        let query = EntityQuery.from('Pledges').where(predicate);
        if (options) {
            if (options.expand) query = query.expand(options.expand);
            if (options.orderBy) query = query.orderBy(options.orderBy);
        }
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getPledgeLines() {
        const predicate = new Predicate('deleted', '==', null);
        const query = EntityQuery.from('PledgeLines').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPledgeGiving() {
        const query = EntityQuery.from('GetPledgeGiving');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getNoAssociatedPledgeGiving() {
        const query = EntityQuery.from('GetNoAssociatedPledgeGiving');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getCloverAccounts() {
        let predicate = new Predicate('deleted', '==', null);
        const query = EntityQuery.from('CloverAccounts').where(predicate).orderBy('id');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }


    async getCommitteeAgendas(memberCode: string, committeeId: number) {
        let predicate = new Predicate('deleted', '==', null);
        const query = EntityQuery.from('GetCommitteeAgendas').withParameters({memberCode, committeeId}).orderByDesc('created');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getCommitteeDocuments(memberCode: string, committeeId: number) {
        let predicate = new Predicate('deleted', '==', null);
        const query = EntityQuery.from('GetCommitteeDocuments').withParameters({memberCode, committeeId}).orderByDesc('created');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getAuctionPaddles(expand?: any) {
        const query = EntityQuery.from('CurrentAuctionPaddles').orderBy('paddleNumber');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getAuctionItems(expand?: any) {
        let predicate = new Predicate('deleted', '==', null);
        const query = EntityQuery.from('AuctionItems').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getAuctionPaddleUserByPaddleId(paddleId: number, expand?: any) {
        let predicate = new Predicate('deleted', '==', null).and('auctionPaddleId', '==', paddleId);
        const query = EntityQuery.from('AuctionPaddleUsers').where(predicate).orderByDesc('created');
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getPurchasesForInvoiceByPaddleUserId(paddleUserId: number) {
        const query = EntityQuery.from('GetPurchasesForInvoiceByPaddleUserId').withParameters({ paddleUserId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getAuctionPurchaseInvoices() {
        const query = EntityQuery.from('GetAuctionPurchaseInvoices');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getDeposits(deposited?: boolean, expand?: any) {
        let predicate = new Predicate('statusId', '!=', ContributionBatch.Statuses.Void);
        if (!deposited) predicate = predicate.and('deposited', '==', null);
        const query = EntityQuery.from('ContributionBatches').expand(expand).where(predicate).orderByDesc('date');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
}
